import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';

import { LocationsDTO } from '@bb/domain/dist/types/dto/OrgController/locations';
import {
  UserRoles,
  AltPhoneTypes,
  ROLES,
  IIncomingTransferClass,
  MemberNumberState,
  TransferState,
  Titles,
  UserLicenseType,
  isUserLicensed,
  IGenericUser,
  ITransferUser,
  UserSubRoles,
} from '@bb/users';

import {
  AuthContext,
  AgentsContext,
  AgentsContextUser,
  ListingLabelContext,
} from 'contexts';
import { updateAlmanacAgentUserSubscription } from 'api/AlmanacController';
import {
  getUserDefaultContacts,
  find as findUser,
  current as currentUser,
  findTransfer,
  getOrgMemberNumberState,
  findOutgoingTransferUser,
} from 'api/UserController';
import { getBoards } from 'api/RetsController';
import { getTeamList, TeamList } from 'api/TeamController';
import { locations as getLocations } from 'api/OrgController';
import {
  getCanCreateNonBillableKey,
  getSingleAccessEnabled,
  getSupraPin,
  getSupraSystemSubscription,
} from 'api/SupraSystemSubscriptionController';
import { useDebounce } from 'hooks';
import { featureEnabled, isAssumeOrgType, makeModalResponsive } from 'utils';
import { message, Modal } from 'components';
import AppointmentConfigDefault from '@bb/domain/dist/types/AppointmentConfigDefault';
import { cloneDeep } from 'lodash';
import SupraSystemSubscription, {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import type {
  IUserFindTransferResponse as TransferDTO,
  IGetOrgMemberNumberStateResponseV2,
  IUserFindTransferResponse,
  IUserCurrentResponse,
  JsonObject,
  GetPortalAnsweringConfigResponse,
} from '@/app-package';
import { CachedOrg } from '@bb/automaton';
import { useSearchParams } from '../ShowingsCalendarPage/hooks';

import {
  UserState,
  UserStateDirty,
  AlertType,
  UserDefaultContact,
  isUserAuthenticated,
  isCurrentUser,
  isCurrentUserBranchAdmin,
  isUserBranchAdmin,
  MemberNumbersState,
  ValidationKeys,
  ValidationValue,
  TabKey,
  isOfTypeTabKey,
  UserViewPageProps,
} from './types';
import { isDate, isFilled, isValidEmail, isValidPassword } from './validators';
import { getDate, transformFrontEndInstructions } from './FrontDeskTab/helpers';
import { shouldRequestTransfer } from './util';
import {
  ERROR_MESSAGES,
  NO_BOARD,
  UNCLAIMED_ORG_NO_ACCESS_PAGES,
  SUPRA_PIN_LENGTH,
  ALT_PHONE_TYPES,
} from './constants';

type UserLabelMap = Record<string, AgentsContextUser>;

type Board = { _id: string; board: string; name: string };

export interface FormContextProviderProps extends UserViewPageProps {
  userId?: string;
  transferId?: string;
  tab?: TabKey;
}

export const initialSettings: Partial<AppointmentConfigDefault> = {
  doubleBookings: false,
  overnightNotice: false,
  notice: 1,
  maxOverlap: -1,
  notifyOverlappingAppt: false,
  bufferSlots: 0,
  showing_instructions: '',
  vendor_instructions: '',
  autoConfirm: false,
  listingAgentConfirm: { enabled: false, excludeFromQueue: false },
  instructions: {
    lights: true,
    card: true,
    shoes: true,
    lock_doors: true,
    callIfLate: true,
    knockFirst: true,
    reco: false,
    confirmation: { details: '' },
  },
  maxDuration: 60,
};

type UserPageSupraNotificationToggles = Pick<
  NonNullable<SupraSystemSubscription['notificationSettings']>,
  'showingStarted' | 'lockboxAssignmentReminder'
>;

export interface FormContextType extends FormContextProviderProps {
  doesUserOwnProfile: boolean;
  /** User is part of answering service or mls staff org */
  isAnsweringOrMlsStaff: boolean;
  /** User has been deleted */
  isDeleted: boolean;
  /**
   * User has a pending outgoing transfer. In otherwords, another org has requested
   * that this user be transferred over to their brokerage
   */
  isOutgoingTransfer: boolean;

  alert: AlertType[];
  transfer: IUserFindTransferResponse;

  activeTab: TabKey;
  setActiveTab: (tab: TabKey) => void;

  saving?: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  working?: boolean;
  setWorking: React.Dispatch<React.SetStateAction<boolean>>;

  memberNumberStateLoading?: boolean;
  setMemberNumberStateLoading: React.Dispatch<React.SetStateAction<boolean>>;

  boards?: Board[];
  boardMap?: Record<string, Board>;
  teams?: TeamList;
  setTeams: (teams: TeamList) => void;
  locations?: LocationsDTO['locationIndex'];
  contacts?: UserDefaultContact[];
  setContacts: React.Dispatch<React.SetStateAction<UserDefaultContact[]>>;
  answeringPortalConfig?: GetPortalAnsweringConfigResponse;
  loading: boolean;
  fetchedUserState?: UserState;
  fetchedUserUnverified?: boolean;
  memberNumberStates: {
    [memberNumberString: string]: IGetOrgMemberNumberStateResponseV2;
  };
  memberNumberMultiMatches: {
    [memberNumberString: string]: {
      multiMatch: true;
    } & IGetOrgMemberNumberStateResponseV2;
  };
  fetchedPrimaryNumberString?: string;
  primaryMemberNumberState?: IGetOrgMemberNumberStateResponseV2;
  debouncedMemberNumbers?: MemberNumbersState;
  primaryNumber?: MemberNumbersState[number];

  userState: UserState;
  setUserState: React.Dispatch<React.SetStateAction<UserState>>;

  userStateDirty: UserStateDirty;
  setUserStateDirty: React.Dispatch<React.SetStateAction<UserStateDirty>>;

  outgoingTransferId?: string;

  firstNameValid: boolean;
  lastNameValid: boolean;
  memberNumbersValid: {
    board: boolean;
    number: boolean;
    mlsUniqueId?: boolean;
  }[];
  memberNumbersExist: boolean;
  memberNumbersAllValid: boolean;
  mobileValid: boolean;
  altPhoneTypeValid: boolean;
  altPhoneNumberValid: boolean;
  emailValidity: { isValid: boolean; errorMessage?: string };
  branchValid: boolean;
  addressValid: boolean;
  branchAccessValid: boolean;
  teamAccessValid: boolean;
  passwordValid: boolean;
  licenseTypeValid: boolean;
  frontDeskValidState: {
    forwardCalls: { enabled: boolean; name: boolean; phone: boolean };
    vacationDates: { enabled: boolean; from: boolean; to: boolean };
    forwardReps: { id: boolean; isStartBeforeExpiry: boolean }[];
    releaseCell: boolean;
    notes: boolean;
    copierCode: boolean;
  };
  userSubRoleValid: boolean;
  waiveActivationFeeReasonValid: boolean;
  supraSubscriptionTypeValid: boolean;
  supraSubscriptionBillingValid: boolean;

  allValid: boolean;

  handleInvalidSubmit: () => void;
  mapBoards: (board?: string) => string;
  showWarningRef: React.MutableRefObject<boolean>;

  showingSettings: Partial<AppointmentConfigDefault>;
  setShowingSettings: React.Dispatch<
    React.SetStateAction<Partial<AppointmentConfigDefault>>
  >;

  supraSubscription: UserPageSupraNotificationToggles & {
    currentPin: string;
    newPin: string;
    confirmingPin: string;
    dirty: boolean;
    showingStarted: boolean;
    lockboxAssignmentReminder: boolean;
    singleAccessEnabled: boolean;
  };
  setSupraSubscription: React.Dispatch<
    React.SetStateAction<
      UserPageSupraNotificationToggles & {
        currentPin: string;
        newPin: string;
        confirmingPin: string;
        dirty: boolean;
        showingStarted: boolean;
        lockboxAssignmentReminder: boolean;
        singleAccessEnabled: boolean;
      }
    >
  >;
  canCreateNonBillableKeys: boolean;
  showForwardReps: boolean;
  setShowForwardReps: React.Dispatch<React.SetStateAction<boolean>>;
  /** Whether the ability to use Single Access has been turned on (in Automaton)
   * for the board the user who being edited belongs to */
  isSingleAccessEnabledForBoard: boolean;
}

/**
 * FormContext is used on four different pages:
 * The Manage Users page in the Admin menu (isEdit). This is the assumed default if all others are undefined.
 * The New User page for creating a user (isNew).
 * The Account Settings for an exisitng user (isProfile).
 * The Incoming Transfer page for viewing an incoming transfer (isIncomingTransfer).
 *
 * @param object Containing the following properties:
 * children: Default React prop, passes in all elements nested inside this JSX element wherever it is used.
 * isNew: If true, the user is on the page for creating a new user.
 * isProfile: If true, the user is on the account settings profile page. Allows the admin/agent to update their own account settings.
 * isIncomingTransfer: If true, the user is on the page for viewing/editing an incoming pending/suggested transfer.
 * userId:
 *  If isNew, the id of the new user they are creating. (undefined)
 *  If isProfile, the id of the user interacting with the app (or of the user they are assumed as).
 *  If isIncomingTransfer,
 *  If none of the above three are true (default 'isEdit'), the id of the existing user they are editing.
 * transferId:
 * tab: Whatever value is shown in the webpage URL inside `tab=`
 */
const FormContext: React.Context<FormContextType> = createContext({} as any);

const newUserDefaults = {
  duplicateNotify: false,
  expiryReminder: false,
  feedbackCollected: true,
  feedbackNotify: true,
  feedbackSendToSeller: false,
} as const;

export const FormContextProvider: React.FC<FormContextProviderProps> = ({
  children,
  isNew,
  isProfile,
  isIncomingTransfer,
  userId,
  transferId,
  tab,
}) => {
  const { auth, onboardingPortalConfig, hasLimitedAccess } =
    useContext(AuthContext);
  const { fetchOrgUsers } = useContext(AgentsContext);
  const { getMemberNumberString } = useContext(ListingLabelContext);

  const [userLabelMap, setUserLabelMap] = useState<UserLabelMap>();
  const isAnsweringOrMlsStaff = useMemo(
    () => isAssumeOrgType(auth.org.type),
    [auth.org.type],
  );

  // This is either the `userId` prop on edit screens or the auth user's id on
  // profile screens.
  // This target user id will be exported by this context as `userId` so child
  // components won't have to handle both cases individually.
  const targetUserId = useMemo(
    () => (isProfile ? auth.id : userId),
    [isProfile, auth.id, userId],
  );

  // Check if user is editing themselves on edit screens
  const doesUserOwnProfile = useMemo(
    () => isProfile || auth.id === userId,
    [isProfile, auth.id, userId],
  );

  // #region Data
  const [activeTab, _setActiveTab] = useState<TabKey>(tab || 'PROFILE');

  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [memberNumberStateLoading, setMemberNumberStateLoading] =
    useState(false);

  const [existingUserLoading, setExistingUserLoading] = useState(false);
  const [boardsLoading, setBoardsLoading] = useState(true);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [contactsLoading, setContactsLoading] = useState(
    !isNew && Boolean(targetUserId),
  );

  const showWarningRef = useRef(false);

  const loading = useMemo(
    () =>
      existingUserLoading ||
      boardsLoading ||
      teamsLoading ||
      locationsLoading ||
      contactsLoading,
    [
      existingUserLoading,
      boardsLoading,
      teamsLoading,
      locationsLoading,
      contactsLoading,
    ],
  );
  const [boards, setBoards] = useState<Board[]>();

  const [boardMap, setBoardMap] = useState<Record<string, Board>>({});

  useEffect(() => {
    const fetchBoards = async () => {
      setBoardsLoading(true);

      const data = await getBoards();
      setBoards(data);

      setBoardMap(
        data.reduce<Record<string, Board>>((acc, cv) => {
          acc[cv._id] = cv;
          return acc;
        }, {}),
      );

      setBoardsLoading(false);
    };
    fetchBoards();
  }, []);

  const [teams, setTeams] = useState<TeamList>();

  useEffect(() => {
    const fetchTeams = async () => {
      setTeamsLoading(true);

      const data = await getTeamList();
      setTeams(data);

      setTeamsLoading(false);
    };
    fetchTeams();
  }, []);

  // Valid locations are locations that are not deleted and are administered by the current user
  const validLocations = useMemo(() => {
    if (ROLES[auth.role] >= ROLES.GLOBAL_ADMIN) {
      return auth.org.locations
        .map((_, idx) => idx)
        .filter(idx => !auth.org.locations[idx].isDeleted)
        .map(idx => idx.toString(10));
    }
    return [...new Set<string>([auth.location, ...(auth.locations || [])])];
  }, [auth.role, auth.location, auth.locations, auth.org.locations]);

  const [locations, setLocations] = useState<LocationsDTO['locationIndex']>();
  useEffect(() => {
    const fetchLocations = async () => {
      setLocationsLoading(true);

      const { locationIndex } = await getLocations();

      const activeLocationIndex = Object.entries(locationIndex).reduce<{
        [key: string]: LocationsDTO['locationIndex'][string];
      }>((acc, [key, location]) => {
        if (!validLocations.includes(key)) return acc;
        acc[key] = location;
        return acc;
      }, {});
      setLocations(activeLocationIndex);

      setLocationsLoading(false);
    };
    fetchLocations();
  }, [validLocations]);

  const [contacts, setContacts] = useState<UserDefaultContact[]>([]);
  useEffect(() => {
    const fetchUserLabels = async () => {
      const orgUsers = await fetchOrgUsers();
      const labelMap = orgUsers.reduce<UserLabelMap>((acc, cur) => {
        acc[cur.id] = cur;
        return acc;
      }, {});
      setUserLabelMap(labelMap);
    };

    if (!userLabelMap) {
      fetchUserLabels();
    }
  }, [userLabelMap, fetchOrgUsers]);

  useEffect(() => {
    if (!userLabelMap || isNew || !targetUserId) return;

    const fetchContacts = async () => {
      setContactsLoading(true);

      const { contacts: contactList } = await getUserDefaultContacts({
        userId: targetUserId,
      });

      setContacts(
        contactList
          .map<UserDefaultContact | null>(contact => {
            if (!userLabelMap[contact.contactId]) {
              // Filters out users from other branches for BA
              return null;
            }
            return { ...contact, ...userLabelMap[contact.contactId] };
          })
          .filter<UserDefaultContact>(
            (contact): contact is UserDefaultContact => Boolean(contact),
          ),
      );
      setContactsLoading(false);
    };
    fetchContacts();
  }, [isNew, targetUserId, userLabelMap]);

  useEffect(() => {
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tab]);

  useEffect(() => {
    if (tab && tab === activeTab) {
      deleteSearchParam('tab');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tab]);

  // Parse activeTab param
  const { getSearchParam, deleteSearchParam } = useSearchParams();
  useEffect(() => {
    const parseSearchParams = async () => {
      const activeTabParam = getSearchParam('activeTab') as string;
      if (getSearchParam('activeTab') && isOfTypeTabKey(activeTabParam)) {
        setActiveTab(activeTabParam);
        deleteSearchParam('activeTab');

        const listingShowingCalendarSubscription =
          (getSearchParam('listingShowingCalendarSubscription') as string) ===
          'true';
        const buyerShowingCalendarSubscription =
          (getSearchParam('buyerShowingCalendarSubscription') as string) ===
          'true';

        if (
          !hasLimitedAccess &&
          (listingShowingCalendarSubscription ||
            buyerShowingCalendarSubscription)
        ) {
          try {
            // When user enables calendar subscription, save automatically on cronofy redirect
            await updateAlmanacAgentUserSubscription({
              listingShowingCalendarSubscription,
              buyerShowingCalendarSubscription,
              showBuyerShowingsAsBusy: true,
              showListingShowingsAsBusy: true,
            });
            message.success('Completed calendar sync');
          } catch (err) {
            message.error('Failed to sync calendar');
          } finally {
            deleteSearchParam('listingShowingCalendarSubscription');
            deleteSearchParam('buyerShowingCalendarSubscription');
          }
        }
      }
    };
    parseSearchParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasLimitedAccess]);

  // #endregion Data

  // #region User state
  const [userState, setUserState] = useState<UserState>({
    role: isAnsweringOrMlsStaff ? UserRoles['Global Admin'] : UserRoles.Agent,
    subRole: '',
    title: onboardingPortalConfig.titles[Titles.Salesperson].value,
    memberNumbers: [{ isPrimary: true }],
    ...(isNew && newUserDefaults),
    ...(isNew && {
      frontEndInstructions: { releaseCell: auth.org.country === 'USA' },
    }),
    team: undefined,
    features: [],
    billingCustomerNumber: '',
    supraSubscription: {
      type: '',
      billing: '',
      assignLockboxesReleaseShackles: false,
      waiveActivationFee: false,
    },
  });
  const [userStateDirty, setUserStateDirty] = useState<UserStateDirty>({
    firstName: false,
    lastName: false,
    mobile: false,
    altPhone: false,
    email: false,
    branch: false,
    team: false,
    address: false,
    password: false,
    frontEndInstructions: {
      forwardCalls: { enabled: false, name: false, phone: false },
      vacationDates: { enabled: false, from: false, to: false },
      forwardReps: [],
      releaseCell: false,
      notes: false,
      copierCode: false,
    },
    licenseType: false,
    features: false,
    subRole: false,
    supraSubscription: {
      type: false,
      billing: false,
      assignLockboxesReleaseShackles: false,
      waiveActivationFeeReason: false,
    },
  });

  const [transfer, setTransfer] = useState<TransferDTO>();

  // If true the existing user is currently part of a pending outgoing transfer
  const [isOutgoingTransfer, setIsOutgoingTransfer] = useState(false);

  // If true, then this board has single access enabled
  const [isSingleAccessEnabledForBoard, setIsSingleAccessEnabledForBoard] =
    useState(false);

  const [supraSubscription, setSupraSubscription] = useState<
    UserPageSupraNotificationToggles & {
      currentPin: string;
      newPin: string;
      confirmingPin: string;
      dirty: boolean;
      singleAccessEnabled: boolean;
    }
  >({
    currentPin: '',
    newPin: '',
    confirmingPin: '',
    dirty: false,
    showingStarted: false,
    lockboxAssignmentReminder: false,
    singleAccessEnabled: true,
  });

  // Handle populating state with existing user
  // Keep track of the unmodified fetched user
  const [fetchedUserState, setFetchedUserState] = useState<UserState>();
  const [fetchedUserUnverified, setFetchedUserUnverified] = useState<boolean>();

  const [showForwardReps, setShowForwardReps] = useState(false);

  useEffect(() => {
    const populateUser = async () => {
      if (!isIncomingTransfer && !isProfile && !userId) {
        return;
      }

      setExistingUserLoading(true);

      let existingUser:
        | undefined
        | JsonObject<IUserCurrentResponse>
        | JsonObject<IGenericUser>
        | JsonObject<ITransferUser>;

      if (isProfile) {
        const [user, { transfer: outgoingTransfer, transferExists }] =
          await Promise.all([
            currentUser(),
            findOutgoingTransferUser({ userId: auth.id }),
          ]);
        existingUser = user;
        setIsOutgoingTransfer(transferExists);
        if (transferExists) setTransfer(outgoingTransfer);
      } else if (isIncomingTransfer && transferId) {
        const transferData = (await findTransfer({
          transferId,
        })) as JsonObject<IIncomingTransferClass>;
        setTransfer(transferData);
        existingUser = { ...transferData.user };
      } else if (userId) {
        const [user, { transfer: outgoingTransfer, transferExists }] =
          await Promise.all([
            findUser(userId),
            findOutgoingTransferUser({ userId }),
          ]);

        existingUser = user;

        const isPendingOutgoingTransfer =
          transferExists && outgoingTransfer?.state === TransferState.Pending;
        setIsOutgoingTransfer(isPendingOutgoingTransfer);

        if (transferExists) setTransfer(outgoingTransfer);
      }

      // This is to type the response as not `{ notAuthenticated: true; }` from IUserCurrentResponse
      if (isUserAuthenticated(existingUser!)) {
        const {
          firstName,
          lastName,
          avatarFilestackId,
          avatarFullFilestackId,
          role,
          licenseType,
          title,
          mls_numbers,
          licenseNumber,
          phone,
          frontEndInstructions,
          officeExtension,
          altPhone,
          altPhoneType,
          email,
          location,
          address,
          billingCustomerNumber,
          team,
          isDeleted,
          disabled,
          isAgentChatHidden,
          offerSettings,
          pronoun,
          duplicateNotify,
          expiryReminder,
          feedbackCollected,
          feedbackNotify,
          feedbackSendToSeller,
          isChatAdmin,
          isChatInactive,
          id,
          org,
          notes,
          subRole,
          features,
        } = existingUser;

        let branchAccess: string[];
        let teamAccess: string[] = [];
        // A lot of type guards to get `locations` from either a branch admin current user or branch admin user or branch admin transfer user
        if (isCurrentUser(existingUser)) {
          if (isCurrentUserBranchAdmin(existingUser)) {
            branchAccess = (existingUser as any).locations;
          }
        } else if (isUserBranchAdmin(existingUser)) {
          branchAccess = existingUser.locations;
        }

        if (existingUser.teams) {
          teamAccess = existingUser.teams.map(teamId => teamId.toString());
        }

        let memberNumbers = mls_numbers as UserState['memberNumbers'];

        if (!memberNumbers.length && licenseNumber) {
          memberNumbers = [
            {
              board: NO_BOARD,
              boardId: undefined,
              number: licenseNumber,
              isPrimary: true,
            },
          ];
        }
        const fetchedState: Partial<UserState> = {
          id,
          // @ts-expect-error: mismatch populated org type
          org: typeof org === 'string' ? org.toString() : org.id.toString(),
          firstName,
          lastName,
          avatarFilestackId,
          avatarFullFilestackId,
          role,
          licenseType,
          title,
          memberNumbers,
          mobile: phone,
          // This is to convert the type from
          // BuildJsonType<IFrontEndInstructions> to IFrontEndInstructions
          frontEndInstructions:
            transformFrontEndInstructions(frontEndInstructions),
          officeExtension,
          altPhone,
          altPhoneType,
          email,
          branch: location,
          branchAccess,
          teamAccess,
          address,
          billingCustomerNumber,
          team: team?.toString(),
          isDeleted,
          disabled,
          isAgentChatHidden,
          offerSettings,
          pronoun,
          duplicateNotify,
          expiryReminder,
          feedbackCollected,
          feedbackNotify,
          feedbackSendToSeller,
          isChatAdmin,
          isChatInactive,
          notes,
          subRole,
          features,
        };

        // Fetch the singleAccess flag from the Onboarding Portal settings specific to this board.
        // Used to determine if the Managed Access section with the Single Access toggle is displayed.
        let singleAccessEnabledForBoard = false;
        if (existingUser.org && auth.org?.onboardingPortalId) {
          try {
            if (onboardingPortalConfig.id) {
              singleAccessEnabledForBoard = await getSingleAccessEnabled({
                orgId:
                  typeof org === 'string'
                    ? org.toString()
                    : // type mismatch for populated org type
                      (org as unknown as CachedOrg).id.toString(),
                onboardingPortalId: onboardingPortalConfig.id,
              });
            }
          } catch {
            // Silent fail - keep default false value
          }
        }

        setIsSingleAccessEnabledForBoard(singleAccessEnabledForBoard); // Store board-level flag separately

        const supraSystemSubscription = existingUser.id
          ? await getSupraSystemSubscription({ userId: existingUser.id })
          : null;

        if (supraSystemSubscription) {
          let fetchedSupraSubscription = supraSystemSubscription;

          const currentSupraSubscription = {
            currentPin: '',
            newPin: '',
            confirmingPin: '',
            dirty: false,
            showingStarted:
              fetchedSupraSubscription?.notificationSettings?.showingStarted ??
              false,
            lockboxAssignmentReminder:
              fetchedSupraSubscription?.notificationSettings
                ?.lockboxAssignmentReminder ?? false,
            singleAccessEnabled:
              fetchedSupraSubscription?.singleAccessEnabled !== false,
          };

          setSupraSubscription(currentSupraSubscription);

          if (fetchedSupraSubscription?.status === 'subscribed') {
            const pin = await getSupraPin(existingUser.id!);
            fetchedSupraSubscription = {
              ...fetchedSupraSubscription,
              // @ts-expect-error adding supra pin here to avoid creating new object
              supraPin: pin,
            };
          }
          fetchedState.supraSubscription = {
            ...fetchedSupraSubscription,
            billing: fetchedSupraSubscription?.billing
              ? 'billable'
              : 'non-billable',
          };
        }

        setFetchedUserState(cloneDeep(fetchedState) as UserState);
        setFetchedUserUnverified(existingUser.isUnverified);
        setUserState(cur => {
          const newState = { ...cur, ...fetchedState };

          // If none of the member numbers are primary, set the first to primary
          if (
            fetchedState.memberNumbers?.length &&
            !fetchedState.memberNumbers.some(
              (i: { isPrimary?: boolean }) => i.isPrimary,
            )
          ) {
            newState.memberNumbers[0].isPrimary = true;
          }

          return newState;
        });

        setShowForwardReps(
          !!fetchedState.frontEndInstructions?.forwardReps?.length,
        );
      }

      setExistingUserLoading(false);
    };

    populateUser();
  }, [
    userId,
    transferId,
    isProfile,
    isIncomingTransfer,
    auth.id,
    // TODO - add setAuth in UserSuggestedUpdateModal.tsx for name updates. Currently only updates auth with email updates.
    auth.firstName,
    auth.lastName,
    auth.email,
  ]);

  // Ensure there's always at least one empty member number item
  useEffect(() => {
    if (!userState.memberNumbers?.length) {
      setUserState(cur => ({ ...cur, memberNumbers: [{}] }));
    }
  }, [userState.memberNumbers]);

  // Ensure there's always exactly one primary number
  useEffect(() => {
    if (
      userState.memberNumbers?.length &&
      !userState.memberNumbers.some(({ isPrimary }) => isPrimary)
    ) {
      setUserState(cur => ({
        ...cur,
        memberNumbers: [
          { ...cur.memberNumbers[0], isPrimary: true },
          ...cur.memberNumbers.slice(1),
        ],
      }));
    }
  }, [userState.memberNumbers]);

  // Set licenseType to LICENSED when switching to agent role
  useEffect(() => {
    if (userState.role === UserRoles.Agent)
      setUserState(cur => ({ ...cur, licenseType: UserLicenseType.LICENSED }));
  }, [userState.role]);

  // Initialize altPhoneType if altPhone is modified
  useEffect(() => {
    if (userState.altPhone && !userState.altPhoneType)
      setUserState(cur => ({ ...cur, altPhoneType: AltPhoneTypes.Direct }));
  }, [userState.altPhone, userState.altPhoneType]);

  // Initialize branch multi-access for branch admin role if a branch has been chosen for the user
  useEffect(() => {
    if (
      userState.role === UserRoles['Branch Admin'] &&
      userState.branch &&
      userState.branchAccess === undefined
    ) {
      setUserState(cur => ({ ...cur, branchAccess: [userState.branch] }));
    }
  }, [userState.branch, userState.role, userState.branchAccess]);
  // #endregion User state

  // #region Primary Member Number
  const debouncedMemberNumbers = useDebounce(
    userState.memberNumbers,
    auth.isCypress ? 20 : 1000,
  );
  const [memberNumberStates, setMemberNumberStates] = useState<{
    [memberNumberString: string]: IGetOrgMemberNumberStateResponseV2;
  }>({});
  const [memberNumberMultiMatches, setMemberNumberMultiMatches] = useState<
    FormContextType['memberNumberMultiMatches']
  >({});

  const primaryNumber = React.useMemo(
    () =>
      debouncedMemberNumbers?.find(
        ({ board, number, isPrimary }) => board && number && isPrimary,
      ),
    [debouncedMemberNumbers],
  );

  useEffect(() => {
    if (fetchedUserState?.isDeleted) return;

    const updateMemberNumbersMap = async (
      board: string | 'NO_BOARD',
      boardId: string | null,
      number: string,
      mlsUniqueId?: string,
    ) => {
      if (board === NO_BOARD || !boardId) return;

      const query = { mlsUniqueId, useV2Logic: true };
      const state = await getOrgMemberNumberState(
        { boardId, number: number.toUpperCase() },
        query,
      );
      const memberNumberString = getMemberNumberString(board, number);

      setMemberNumberStates(cur => ({ ...cur, [memberNumberString]: state }));
      if (state.multiMatch === true) {
        setMemberNumberMultiMatches(cur => ({
          ...cur,
          [memberNumberString]: state,
        }));
      }
    };

    debouncedMemberNumbers.forEach(memberNumber => {
      const { board, boardId, number, mlsUniqueId } = memberNumber;
      if (board === NO_BOARD || !board || !boardId || !number?.trim()) return;

      const memberNumberString = getMemberNumberString(board, number);
      const memberNumberState = memberNumberStates[memberNumberString];
      const hasSetMlsUniqueId =
        mlsUniqueId &&
        memberNumberState &&
        (memberNumberState.multiMatch === true ||
          (memberNumberState.mlsUniqueId || mlsUniqueId) !== mlsUniqueId);
      if (memberNumberState === undefined) {
        // Not sending mlsUniqueId in case its a multi match
        // and we have not yet retrieved the possible list of mlsUniqueIds
        updateMemberNumbersMap(board, boardId, number);
      } else if (hasSetMlsUniqueId) {
        updateMemberNumbersMap(board, boardId, number, mlsUniqueId);
      }
    });
  }, [fetchedUserState?.isDeleted, debouncedMemberNumbers, memberNumberStates]);

  // Ensure save button is disabled until all member number states have been populated
  useEffect(() => {
    if (!memberNumberStateLoading || !userState.memberNumbers?.length) return;
    if (
      userState.memberNumbers.every(memberNumber => {
        if (!memberNumber.board || !memberNumber.number) return false;

        // No need to check memberNumber when selecting No Board
        if (memberNumber.board === NO_BOARD) return true;

        const key = getMemberNumberString(
          memberNumber.board,
          memberNumber.number,
        );

        return memberNumberStates[key];
      })
    ) {
      setMemberNumberStateLoading(false);
    }
  }, [memberNumberStateLoading, userState.memberNumbers, memberNumberStates]);

  const fetchedPrimaryNumberString = useMemo(() => {
    const primary = debouncedMemberNumbers.find(
      memberNumber => memberNumber.isPrimary,
    );
    if (!primary?.board || !primary?.number) return undefined;
    if (primary) {
      const key = getMemberNumberString(primary.board, primary.number);
      if (memberNumberStates[key]) return key;
    }
    return undefined;
  }, [debouncedMemberNumbers, memberNumberStates]);
  const primaryMemberNumberState = useMemo(
    () => memberNumberStates[fetchedPrimaryNumberString],
    [fetchedPrimaryNumberString, memberNumberStates],
  );
  // #endregion Primary Member Number

  // #region Validation

  const firstNameValid = useMemo(
    () => isFilled(userState.firstName),
    [userState.firstName],
  );
  const lastNameValid = useMemo(
    () => isFilled(userState.lastName),
    [userState.lastName],
  );
  const memberNumbersValid = useMemo(() => {
    // user cannot have duplicate number in same board
    // eg: { TRREB: 12334, TRREB: 12334 }
    const duplicateBoardNumbers = userState.memberNumbers.reduce<{
      [boardId: string]: { [mlsNumber: string]: number };
    }>((acc, curr) => {
      const { boardId, number } = curr;

      if (!boardId || !number) return acc;

      if (!acc[boardId]) acc[boardId] = {};

      acc[boardId][number] = (acc[boardId][number] || 0) + 1;
      return acc;
    }, {});

    return userState.memberNumbers.map(item => {
      const boardExists = Boolean(item.board);
      const boardIdExists = Boolean(item.boardId);
      const numberExists = Boolean(item.number);
      const isNoBoard = item.board === NO_BOARD;
      const key =
        boardExists && numberExists
          ? getMemberNumberString(item.board, item.number)
          : '';
      const memberNumberState = memberNumberStates[key];
      return {
        number:
          !isNoBoard && boardExists
            ? numberExists &&
              duplicateBoardNumbers[item.boardId][item.number] === 1
            : numberExists,
        board: boardExists,
        boardId: boardIdExists,
        mlsUniqueId: !memberNumberState || !memberNumberState.multiMatch,
      };
    });
  }, [userState.memberNumbers, memberNumberStates]);
  const memberNumbersExist = useMemo(
    () => userState.memberNumbers.length > 0,
    [userState.memberNumbers],
  );
  const memberNumbersAllValid = useMemo(() => {
    if (
      userState.licenseType &&
      [
        UserLicenseType.UNLICENSED,
        UserLicenseType.UNLICENSED_WITH_MEMBER_NUMBER,
      ].includes(userState.licenseType)
    )
      return true;
    if (!memberNumbersExist) return false;
    return !memberNumbersValid.some(
      item => !item.board || !item.number || !item.mlsUniqueId,
    );
  }, [userState.licenseType, memberNumbersExist, memberNumbersValid]);
  const mobileValid = useMemo(
    () => userState.mobile?.replace(/\D/g, '').length === 10,
    [userState.mobile],
  );
  const altPhoneTypeValid = useMemo(
    () =>
      !userState.altPhone?.trim() ||
      (userState.altPhoneType &&
        ALT_PHONE_TYPES.includes(userState.altPhoneType)),
    [userState.altPhone, userState.altPhoneType],
  );
  const altPhoneNumberValid = useMemo(
    () =>
      !userState.altPhone?.trim() ||
      userState.altPhone.replace(/\D/g, '').length === 10,
    [userState.altPhone],
  );

  const [emailValidity, setEmailValidity] = useState<{
    isValid: boolean;
    errorMessage?: string;
  }>({ isValid: false, errorMessage: ERROR_MESSAGES.email });
  const debouncedEmail = useDebounce(userState.email, 1500);
  useEffect(() => {
    const validateEmail = async (email: string) => {
      const shouldRequestTransferStatus = shouldRequestTransfer({
        primaryMemberNumberState,
        primaryNumber,
        firstNameValid,
        lastNameValid,
        isNew,
        licenseType: userState.licenseType,
      });
      const emailValidityTemp = await isValidEmail(
        email,
        shouldRequestTransferStatus,
        isNew,
        fetchedUserState?.email,
      );
      setEmailValidity(emailValidityTemp);
      setWorking(false);
    };

    validateEmail(debouncedEmail);
  }, [
    debouncedEmail,
    firstNameValid,
    lastNameValid,
    isNew,
    userState.licenseType,
    primaryMemberNumberState,
    primaryNumber,
    fetchedUserState?.email,
  ]);
  const branchValid = useMemo(
    () => userState.branch !== undefined,
    [userState.branch],
  );
  const addressValid = useMemo(() => {
    const addr = userState.address;
    if (addr === undefined) return false;
    return typeof addr === 'string' ? addr.trim().length >= 1 : Boolean(addr);
  }, [userState.address]);
  const licenseTypeValid = useMemo(
    () => userState.licenseType !== undefined,
    [userState.licenseType],
  );
  const supraSubscriptionValid = useMemo(
    () =>
      !(
        supraSubscription?.newPin ||
        supraSubscription?.confirmingPin ||
        supraSubscription?.currentPin
      ) ||
      (supraSubscription?.newPin === supraSubscription?.confirmingPin &&
        supraSubscription?.newPin.length === SUPRA_PIN_LENGTH &&
        supraSubscription?.currentPin.length === SUPRA_PIN_LENGTH),
    [
      supraSubscription?.newPin,
      supraSubscription?.confirmingPin,
      supraSubscription?.currentPin,
    ],
  );

  // If subscription status is undefined or unsubscribed, type is not required.
  // Otherwise, type must be set.
  const supraSubscriptionTypeValid = useMemo(
    () =>
      !userState.supraSubscription?.status ||
      userState.supraSubscription?.status ===
        SupraSystemSubscriptionStatus.unsubscribed
        ? true
        : !!userState.supraSubscription?.type,
    [userState.supraSubscription],
  );

  const [canCreateNonBillableKeys, setCanCreateNonBillableKey] = useState(true);
  useEffect(() => {
    (async () => {
      if (
        auth.org.features?.includes('SUPRA_MVP') &&
        featureEnabled('supraAddUserFeature') &&
        auth.parentOrgType === 'mlsStaff'
      ) {
        const res = await getCanCreateNonBillableKey(auth.org.id);
        setCanCreateNonBillableKey(res);
      }
    })();
  }, [auth.org]);

  // If unsubscribed user does not intend to subscribe then billing value not required.
  const supraSubscriptionBillingValid = useMemo(() => {
    if (
      (!userState.supraSubscription?.status ||
        userState.supraSubscription?.status ===
          SupraSystemSubscriptionStatus.unsubscribed) &&
      !userState.supraSubscription?.type
    ) {
      return true;
    }

    if (
      userState.supraSubscription?.billing === 'non-billable' &&
      userState.subRole !== UserSubRoles['SupraNET Manual Coop']
    ) {
      return canCreateNonBillableKeys;
    }
    return userState.supraSubscription?.billing !== '';
  }, [
    userState.supraSubscription,
    canCreateNonBillableKeys,
    userState.subRole,
  ]);

  const waiveActivationFeeReasonValid = useMemo(
    () =>
      userState.supraSubscription?.waiveActivationFee
        ? isFilled(userState.supraSubscription?.waiveActivationFeeReason)
        : true,
    [
      userState.supraSubscription?.waiveActivationFee,
      userState.supraSubscription?.waiveActivationFeeReason,
    ],
  );

  // If the subscription type exists and is LIMITED or MANAGED ACCESS, check whether the Sub Key type is valid. Otherwise, it always is.
  const userSubRoleValid = useMemo(
    () =>
      userState.supraSubscription?.type &&
      [
        SupraSystemSubscriptionType.LIMITED,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ].includes(userState.supraSubscription?.type)
        ? !!userState.subRole
        : true,
    [userState.supraSubscription, userState.subRole],
  );
  const branchAccessValid = useMemo(() => {
    // Branch access not needed for other roles
    if (userState.role !== UserRoles['Branch Admin']) return true;
    // If there's no branch yet, we can fail the validation there - the
    // necessary items for this field are still indeterminate
    if (userState.branch === undefined) return true;
    // Branch multi-access must include the user's own branch
    return userState.branchAccess?.includes(userState.branch);
  }, [userState.branchAccess, userState.branch, userState.role]);

  const teamAccessValid = useMemo(() => {
    // if user is TA and has no teams return false
    if (
      userState.role === UserRoles['Team Admin'] &&
      !userState.teamAccess?.length
    ) {
      return false;
    }
    return true;
  }, [userState.teamAccess, userState.role]);

  const passwordValid = useMemo(() => {
    if (isNew) {
      // When creating a new agent, the password field is hidden and does not need to be validated
      if (!userState.password && userState.role === UserRoles.Agent)
        return true;

      if (isAssumeOrgType(auth.org.type)) {
        return true;
      }
    }

    /**
     * Can optionally provide a password on create for other roles
     *
     * When updating a user the field should be optional
     *
     * The field itself has validation so a falsy check should be okay here
     */
    return !userState.password || isValidPassword(userState.password);
  }, [isNew, userState.password, userState.role, auth?.org.type]);
  // #endregion Validation

  // #region FrontDesk
  const [frontDeskValidState, setFrontDeskValidState] = useState<
    FormContextType['frontDeskValidState']
  >({
    forwardCalls: { enabled: false, name: false, phone: false },
    vacationDates: { enabled: false, from: false, to: false },
    forwardReps: [],
    releaseCell: false,
    notes: false,
    copierCode: false,
  });

  const [frontDeskValid, setFrontDeskValid] = useState(
    !userState.frontEndInstructions,
  );

  useEffect(() => {
    setFrontDeskValidState(prev => {
      let isValid = true;
      const copy = { ...prev };

      // Check forwardReps
      const forwardRepsValidity: FormContextType['frontDeskValidState']['forwardReps'] =
        [];
      userState.frontEndInstructions?.forwardReps?.forEach(rep => {
        const isRepValidState = {
          id: isFilled(rep.id),
          isStartBeforeExpiry:
            !rep.forwardRepsStart ||
            !rep.forwardRepsExpiry ||
            getDate(rep.forwardRepsStart) < getDate(rep.forwardRepsExpiry),
        };

        if (!isRepValidState.id || !isRepValidState.isStartBeforeExpiry) {
          isValid = false;
        }
        forwardRepsValidity.push({ ...isRepValidState });
      });
      copy.forwardReps = forwardRepsValidity;

      // Check forwardCalls
      if (userState.frontEndInstructions?.forwardCalls?.enabled) {
        copy.forwardCalls.name = isFilled(
          userState.frontEndInstructions?.forwardCalls.name,
        );
        copy.forwardCalls.phone = isFilled(
          userState.frontEndInstructions?.forwardCalls.phone,
        );
        if (!copy.forwardCalls.name || !copy.forwardCalls.phone) {
          isValid = false;
        }
      }

      // Check vacations
      if (userState.frontEndInstructions?.vacationDates?.enabled) {
        copy.vacationDates.to = isDate(
          userState.frontEndInstructions?.vacationDates?.to,
        );
        copy.vacationDates.from = isDate(
          userState.frontEndInstructions?.vacationDates?.from,
        );
        if (!copy.vacationDates.to || !copy.vacationDates.from) {
          isValid = false;
        }
      }

      setFrontDeskValid(isValid);
      return copy;
    });
  }, [
    userState.frontEndInstructions?.vacationDates,
    userState.frontEndInstructions?.forwardReps,
    userState.frontEndInstructions?.forwardCalls,
  ]);
  // #endregion FrontDesk

  // #region Submit Validation
  const validationMap = useMemo(
    () =>
      ({
        firstNameValid: {
          field: firstNameValid,
          dirtyKey: 'firstName',
          tabKey: 'PROFILE',
        },
        lastNameValid: {
          field: lastNameValid,
          dirtyKey: 'lastName',
          tabKey: 'PROFILE',
        },
        mobileValid: {
          field: mobileValid,
          dirtyKey: 'mobile',
          tabKey: 'PROFILE',
        },
        altPhoneTypeValid: {
          field: altPhoneTypeValid,
          dirtyKey: 'altPhone',
          tabKey: 'PROFILE',
        },
        altPhoneNumberValid: {
          field: altPhoneNumberValid,
          dirtyKey: 'altPhone',
          tabKey: 'PROFILE',
        },
        emailValid: {
          field: emailValidity.isValid,
          dirtyKey: 'email',
          tabKey: 'PROFILE',
        },
        passwordValid: {
          field: passwordValid,
          dirtyKey: 'password',
          tabKey: 'PROFILE',
        },
        branchValid: {
          field: branchValid,
          dirtyKey: 'branch',
          tabKey: 'PROFILE',
        },
        addressValid: {
          field: addressValid,
          dirtyKey: 'address',
          tabKey: 'PROFILE',
        },
        branchAccessValid: {
          field: branchAccessValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
        },
        teamAccessValid: {
          field: teamAccessValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
        },
        waiveActivationFeeReasonValid: {
          field: waiveActivationFeeReasonValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
          getDirtyState: () => ({
            supraSubscription: {
              ...userStateDirty.supraSubscription,
              waiveActivationFeeReason: true,
            },
          }),
        },
        memberNumbersAllValid: {
          field: memberNumbersAllValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
          getDirtyUserStateField: () => ({
            memberNumbers: userState.memberNumbers.map(memberNumber => ({
              ...memberNumber,
              boardDirty: true,
              numberDirty: true,
              mlsUniqueIdDirty: true,
            })),
          }),
        },
        frontDeskValid: {
          field: frontDeskValid,
          dirtyKey: null,
          tabKey: 'FRONT_DESK_INSTRUCTIONS',
          getDirtyState: () => {
            const state: Partial<UserStateDirty> = {};
            const dirtyInstructions: Partial<
              UserStateDirty['frontEndInstructions']
            > = {};
            const { forwardCalls, vacationDates } =
              userStateDirty.frontEndInstructions;
            const { forwardReps } = userState.frontEndInstructions;

            if (forwardCalls) {
              dirtyInstructions.forwardCalls = {
                enabled: true,
                name: true,
                phone: true,
              };
            }
            if (vacationDates) {
              dirtyInstructions.vacationDates = {
                enabled: true,
                from: true,
                to: true,
              };
            }
            if (forwardReps) {
              const dirtyRep = {
                id: true,
                forwardRepsExpiry: true,
                forwardRepsStart: true,
              };
              dirtyInstructions.forwardReps = forwardReps.map(() => ({
                ...dirtyRep,
              }));
            }
            dirtyInstructions.releaseCell = true;
            dirtyInstructions.notes = true;
            dirtyInstructions.copierCode = true;

            state.frontEndInstructions =
              dirtyInstructions as UserStateDirty['frontEndInstructions'];
            return state;
          },
        },
        supraSubscriptionValid: {
          field: supraSubscriptionValid,
          dirtyKey: null,
          tabKey: 'SUPRA_SUBSCRIPTION',
        },
        supraSubscriptionTypeValid: {
          field: supraSubscriptionTypeValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
          getDirtyState: () => ({
            supraSubscription: {
              ...userStateDirty.supraSubscription,
              type: true,
              billing: true,
            },
          }),
        },
        supraSubscriptionBillingValid: {
          field: supraSubscriptionBillingValid,
          dirtyKey: null,
          tabKey: 'PROFILE',
        },
        userSubRoleValid: {
          field: userSubRoleValid,
          dirtyKey: 'subRole',
          tabKey: 'PROFILE',
        },
        licenseTypeValid: {
          field: licenseTypeValid,
          dirtyKey: 'licenseType',
          tabKey: 'PROFILE',
        },
      }) as Record<ValidationKeys, ValidationValue>,
    [
      firstNameValid,
      lastNameValid,
      mobileValid,
      altPhoneTypeValid,
      altPhoneNumberValid,
      emailValidity.isValid,
      passwordValid,
      branchValid,
      addressValid,
      branchAccessValid,
      teamAccessValid,
      waiveActivationFeeReasonValid,
      frontDeskValid,
      memberNumbersAllValid,
      supraSubscriptionValid,
      licenseTypeValid,
      userState.frontEndInstructions,
      userState.memberNumbers,
      userStateDirty.frontEndInstructions,
      userSubRoleValid,
      userStateDirty.supraSubscription,
      supraSubscriptionTypeValid,
      supraSubscriptionBillingValid,
    ],
  );

  const allValidFields: ValidationKeys[] = useMemo(() => {
    if (isAnsweringOrMlsStaff)
      return [
        'firstNameValid',
        'lastNameValid',
        'mobileValid',
        'altPhoneTypeValid',
        'altPhoneNumberValid',
        'emailValid',
        'passwordValid',
      ];

    if (isProfile)
      return [
        'firstNameValid',
        'lastNameValid',
        'mobileValid',
        'altPhoneTypeValid',
        'altPhoneNumberValid',
        'emailValid',
        'passwordValid',
        'frontDeskValid',
        'supraSubscriptionValid',
      ];

    if (isNew && userState.role === UserRoles.Agent) {
      // No password is set for agents.
      // For non-agents password is optional.
      return [
        'firstNameValid',
        'lastNameValid',
        'memberNumbersAllValid',
        'mobileValid',
        'altPhoneTypeValid',
        'altPhoneNumberValid',
        'emailValid',
        'branchValid',
        'addressValid',
        'branchAccessValid',
        'teamAccessValid',
        'frontDeskValid',
        'licenseTypeValid',
        'waiveActivationFeeReasonValid',
        ...(auth.org.features?.includes('SUPRA_MVP') &&
        featureEnabled('supraAddUserFeature') &&
        auth.parentOrgType === 'mlsStaff'
          ? ([
              'supraSubscriptionTypeValid',
              'supraSubscriptionBillingValid',
              'userSubRoleValid',
            ] as ValidationKeys[])
          : []),
      ];
    }

    return [
      'firstNameValid',
      'lastNameValid',
      'memberNumbersAllValid',
      'mobileValid',
      'altPhoneTypeValid',
      'altPhoneNumberValid',
      'emailValid',
      'branchValid',
      'addressValid',
      'branchAccessValid',
      'teamAccessValid',
      'passwordValid',
      'frontDeskValid',
      'licenseTypeValid',
      'waiveActivationFeeReasonValid',
      ...(auth.org.features?.includes('SUPRA_MVP') &&
      featureEnabled('supraAddUserFeature') &&
      auth.parentOrgType === 'mlsStaff'
        ? ([
            'supraSubscriptionTypeValid',
            'supraSubscriptionBillingValid',
            'userSubRoleValid',
          ] as ValidationKeys[])
        : []),
    ];
  }, [isAnsweringOrMlsStaff, isProfile, isNew, userState?.role, auth]);

  /* The code to go through all of the fields if some are not valid and alert the user
     which ones they need to fix. */
  const handleInvalidSubmit = () => {
    const valuesToDirty: Partial<UserStateDirty> = {};
    const dirtyUserStateValues: Partial<UserState> = {};
    const conflictingTabMap: Partial<Record<TabKey, boolean>> = {};

    let shouldUpdateUserState = false;
    let errorCount = 0;

    allValidFields.forEach(field => {
      const {
        dirtyKey,
        tabKey,
        field: validField,
        getDirtyUserStateField,
        getDirtyState,
      } = validationMap[field];

      if (validField) return;

      if (dirtyKey) {
        valuesToDirty[dirtyKey] = true;
      } else if (getDirtyState) {
        Object.assign(valuesToDirty, getDirtyState());
      } else if (getDirtyUserStateField) {
        Object.assign(dirtyUserStateValues, getDirtyUserStateField());
        shouldUpdateUserState = true;
      }
      conflictingTabMap[tabKey] = true;
      errorCount += 1;
    });

    // Dirty invalid fields so that they are put into error state
    setUserStateDirty(cur => ({ ...cur, ...valuesToDirty }));
    if (shouldUpdateUserState) {
      setUserState(cur => ({ ...cur, ...dirtyUserStateValues }));
    }

    // Switch to conflicting tab if needed
    if (!conflictingTabMap[activeTab] && activeTab !== 'SUPRA_SUBSCRIPTION') {
      setActiveTab(
        Object.keys(conflictingTabMap).find(key => key !== activeTab) as TabKey,
      );
    }

    // Update error count to compensate for fields that don't appear until another is valid
    if (
      allValidFields.includes('branchValid') &&
      !branchValid &&
      !addressValid
    ) {
      errorCount -= 1;
    }

    if (activeTab === 'SUPRA_SUBSCRIPTION') {
      message.warning('Confirm new PIN: Fields do not match');
    } else {
      message.warning(
        `At least ${errorCount} field${
          errorCount !== 1 ? 's need to be revised' : ' needs to be revised'
        }`,
      );
    }
  };
  // #endregion Submit Validation

  // #region Alert
  const alert: AlertType[] = useMemo(() => {
    const result: AlertType[] = [];
    if (isIncomingTransfer) return ['incomingTransfer'];
    if (
      isNew &&
      isUserLicensed(userState) &&
      userState.memberNumbers.some(
        ({ board, number, boardDirty, numberDirty, isPrimary }) =>
          isPrimary &&
          board &&
          number &&
          boardDirty &&
          numberDirty &&
          fetchedPrimaryNumberString === getMemberNumberString(board, number),
      ) &&
      primaryMemberNumberState.multiMatch !== true &&
      primaryMemberNumberState.inBrokerBay &&
      !primaryMemberNumberState.inOrg &&
      !primaryMemberNumberState.isPendingTransfer &&
      memberNumbersAllValid
    ) {
      return ['suggestTransfer'];
    }
    if (isOutgoingTransfer) {
      result.push('outgoingTransfer');
    }
    if (
      userState.licenseType === UserLicenseType.LICENSED &&
      userState.memberNumbers.find(
        ({ board, number, boardDirty, numberDirty, isPrimary }) =>
          isPrimary &&
          board &&
          number &&
          (!isNew || (boardDirty && numberDirty)) &&
          fetchedPrimaryNumberString === getMemberNumberString(board, number) &&
          primaryMemberNumberState.multiMatch !== true &&
          (primaryMemberNumberState.state === MemberNumberState.none ||
            primaryMemberNumberState.inRoster === false ||
            primaryMemberNumberState.inRosterOrg === false),
      )
    ) {
      result.push('unverified');
    }
    if (UNCLAIMED_ORG_NO_ACCESS_PAGES.includes(activeTab) && hasLimitedAccess) {
      result.push('noAccess');
    }
    return result;
  }, [
    activeTab,
    fetchedPrimaryNumberString,
    isIncomingTransfer,
    isNew,
    isOutgoingTransfer,
    memberNumbersAllValid,
    hasLimitedAccess,
    primaryMemberNumberState,
    userState,
  ]);
  // #endregion Alert

  // #region Showing Settings
  const [showingSettings, setShowingSettings] =
    useState<Partial<AppointmentConfigDefault>>(initialSettings);

  const mapBoards = (board?: string) => {
    if (!board) return;

    const trebBoards = ['BREB', 'DRAR'];
    if (trebBoards.includes(board)) return 'PropTx';
    return board;
  };

  const setActiveTab = useCallback(
    (chosenTab: TabKey) => {
      if (activeTab === 'LIVE_CONCIERGE_SERVICE' && showWarningRef.current) {
        makeModalResponsive(
          Modal.confirm({
            title: 'Are you sure you want to leave?',
            content:
              'Your live concierge service has not been activated. Are you sure you want to leave this page?',
            okText: 'Yes',
            cancelText: 'Cancel',
            centered: true,
            onOk: () => {
              _setActiveTab(chosenTab);
            },
          }),
        );
      } else if (
        activeTab === 'SUPRA_SUBSCRIPTION' &&
        supraSubscription.dirty
      ) {
        if (window.confirm('Are you sure you want to leave without saving?')) {
          setSupraSubscription({
            currentPin: '',
            newPin: '',
            confirmingPin: '',
            dirty: false,
            showingStarted:
              userState.supraSubscription?.notificationSettings
                ?.showingStarted ?? false,
            lockboxAssignmentReminder:
              userState.supraSubscription?.notificationSettings
                ?.lockboxAssignmentReminder ?? false,
            singleAccessEnabled:
              userState.supraSubscription?.singleAccessEnabled !== false,
          });
          _setActiveTab(chosenTab);
        }
      } else {
        _setActiveTab(chosenTab);
      }
    },
    [activeTab, supraSubscription.dirty],
  );

  // Mark dirty fields to let users know what fields transfer suggestions require
  useEffect(() => {
    if (alert.includes('suggestTransfer')) {
      setUserStateDirty(cur => ({
        ...cur,
        email: true,
        firstName: true,
        lastName: true,
        branch: true,
        address: true,
        mobile: true,
      }));
    }
  }, [alert]);

  if (isProfile && auth.isAssumedNotBB) return null;

  return (
    <FormContext.Provider
      value={{
        isNew,
        isProfile,
        isIncomingTransfer,
        userId: targetUserId,
        doesUserOwnProfile,
        transferId,
        outgoingTransferId: isOutgoingTransfer ? transfer?.id : undefined,
        transfer,

        alert,

        activeTab,
        setActiveTab,

        saving,
        setSaving,

        working,
        setWorking,

        memberNumberStateLoading,
        setMemberNumberStateLoading,

        boards,
        boardMap,
        teams,
        setTeams,
        locations,
        contacts,
        setContacts,
        loading,
        memberNumberStates,
        memberNumberMultiMatches,
        fetchedPrimaryNumberString,
        primaryNumber,
        primaryMemberNumberState,
        debouncedMemberNumbers,

        userState,
        setUserState,
        fetchedUserState,
        fetchedUserUnverified,

        userStateDirty,
        setUserStateDirty,

        firstNameValid,
        lastNameValid,
        memberNumbersValid,
        memberNumbersExist,
        memberNumbersAllValid,
        mobileValid,
        altPhoneTypeValid,
        altPhoneNumberValid,
        emailValidity,
        branchValid,
        addressValid,
        branchAccessValid,
        teamAccessValid,
        passwordValid,
        licenseTypeValid,
        frontDeskValidState,
        userSubRoleValid,
        waiveActivationFeeReasonValid,
        supraSubscriptionBillingValid,
        supraSubscriptionTypeValid,
        canCreateNonBillableKeys,

        allValid: allValidFields.every(key => validationMap[key].field),
        isAnsweringOrMlsStaff,
        isDeleted: fetchedUserState?.isDeleted,
        isOutgoingTransfer,

        handleInvalidSubmit,
        mapBoards,
        showWarningRef,

        showingSettings,
        setShowingSettings,

        supraSubscription,
        setSupraSubscription,

        showForwardReps,
        setShowForwardReps,

        isSingleAccessEnabledForBoard,
      }}
    >
      {children}
    </FormContext.Provider>
  );
};

export default FormContext;
